import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// Listed by Miro's MCP server (https://mcp.miro.com/) through P0's gateway on
// 2026-11-02 (INT-1106), signed in as a board owner in "Dev team".
// Annotations are as Miro sent them. The gateway wraps each tool's input as
// `{ request_id, call }` and puts its access instructions before each
// description, so this keeps Miro's own `call` schema, less the gateway's
// `$id`, and Miro's own description. Only the tools a grant gives
// (`MIRO_AI_GRANTED_TOOLS`), in its order.
export const miroAiMcpTools: McpTool[] = [
  {
    name: "board_create",
    description:
      "Create a new Miro board. To place the board inside a space, pass parent_space_url - either the space URL or the space content item id. Creating it in the space directly saves the extra board_move call that creating it at the team root would need. IMPORTANT: Always confirm with the user before creating a board. This action creates a new board and cannot be undone.",
    inputSchema: {
      type: "object",
      properties: {
        name: {
          description: "Name of the new board",
          maxLength: 60,
          minLength: 1,
          type: "string",
        },
        description: {
          default: null,
          description: "Optional description for the board",
          maxLength: 2000,
          type: "string",
          nullable: true,
        },
        parent_space_url: {
          default: null,
          description:
            "Space to create the content inside, given either as a full Miro space URL (e.g., 'https://miro.com/app/dashboard/space/0VM5RHCprOu2MnOHXLqp3f') or as the space content item id on its own (e.g., '0VM5RHCprOu2MnOHXLqp3f'). Omit to create at the team root instead.",
          minLength: 1,
          type: "string",
          nullable: true,
        },
        icon_emoji: {
          default: null,
          description:
            'Optional single emoji to use as the board icon, e.g. "🚀". Set it when the user asks for an icon; otherwise leave it unset.',
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["name"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create New Board",
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "board_create_format",
    description:
      "Create a typed board format, such as a table, timeline, kanban, document, diagram, prototyping container, slide container, activities board, or embed. Use this tool when the user asks for the document/table/diagram itself as a standalone piece of content (e.g. 'create a doc in Miro about X', 'make me a table of Y'). Prefer it over adding a doc, table or diagram widget to an existing board: those tools are for adding content onto a board the user is already working on. To place the format inside a space, pass parent_space_url - either the space URL or the space content item id. Creating it in the space directly saves the extra board_move call that creating it at the team root would need. IMPORTANT: Always confirm with the user before creating content. This action cannot be undone.",
    inputSchema: {
      type: "object",
      properties: {
        name: {
          description: "Name of the new format",
          maxLength: 255,
          minLength: 1,
          type: "string",
        },
        format_type: {
          description: "The kind of format to create",
          enum: [
            "embed",
            "table",
            "timeline",
            "kanban",
            "document",
            "diagram",
            "prototyping_container",
            "slide_container",
            "activities",
          ],
          type: "string",
        },
        parent_space_url: {
          default: null,
          description:
            "Space to create the content inside, given either as a full Miro space URL (e.g., 'https://miro.com/app/dashboard/space/0VM5RHCprOu2MnOHXLqp3f') or as the space content item id on its own (e.g., '0VM5RHCprOu2MnOHXLqp3f'). Omit to create at the team root instead.",
          minLength: 1,
          type: "string",
          nullable: true,
        },
        embed_url: {
          default: null,
          description:
            "Source URL to embed (only used when format_type is 'embed')",
          type: "string",
          nullable: true,
        },
        icon_emoji: {
          default: null,
          description:
            'Optional single emoji to use as the icon, e.g. "🚀". Set it when the user asks for an icon; otherwise leave it unset.',
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["name", "format_type"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Board Format",
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "board_get_space",
    description: "Find which space a board belongs to.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Get Board Space",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "board_move",
    description:
      "Move an existing Miro board under a space or folder. Use this tool when a user asks to move a board into a space or under a folder. Provide the board and the content item id of the destination space or folder.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        target_parent_content_item_id: {
          description: "Content item id of the destination space or folder.",
          minLength: 1,
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "target_parent_content_item_id"],
      additionalProperties: false,
    },
    annotations: {
      title: "Move Board",
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "board_restore",
    description: "Restore one or more boards from trash.",
    inputSchema: {
      type: "object",
      properties: {
        board_urls: {
          description: "Full Miro board URLs to restore from trash",
          items: {
            type: "string",
          },
          maxItems: 100,
          minItems: 1,
          type: "array",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["board_urls"],
      additionalProperties: false,
    },
    annotations: {
      title: "Restore Boards",
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "board_update_metadata",
    description:
      "Update a board's title, description and/or icon emoji. Omitted fields are left unchanged. To remove the board's icon, pass an empty string as icon_emoji.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        title: {
          default: null,
          description: "New board title",
          maxLength: 255,
          type: "string",
          nullable: true,
        },
        description: {
          default: null,
          description: "New board description",
          maxLength: 2000,
          type: "string",
          nullable: true,
        },
        icon_emoji: {
          default: null,
          description:
            'New single-emoji icon for the board, e.g. "🚀". Omit to leave the icon unchanged; pass an empty string to remove it.',
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Update Board Metadata",
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "canvas_create_from_svg",
    description:
      "Create board items from a canvas-composer SVG document. Parses the SVG into Miro widgets -- shapes, stickies, text, connectors, frames, tables, docs, images, slide decks, AND structured Mermaid diagrams (flowchart, ERD, UML class/sequence, authored as a <foreignObject data-type=\"diagram\"> with a Mermaid body). This is the primary tool for creating ANY board content, diagrams and presentations included: 'create a diagram' / 'draw a flowchart' are handled here, NOT by the legacy diagram_* tools, and 'build a slide deck' / 'make a presentation' are handled here too. Returns a result_svg with data-miro-id stamped on every svg element for the next iteration.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        svg: {
          description: "A board written in the Canvas Composer SVG format.",
          maxLength: 200000,
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "svg"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Board from SVG",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "canvas_get_canvas_composer_skill",
    description:
      "Get the board-authoring workflow instructions and the DSL (Domain-Specific Language) format specification for creating board items. REQUIRED and FIRST: call this before canvas_create_from_svg, and before canvas_load_format_skill. The first call (no arguments) routes you to the right workflow step; follow the returned instructions and call again with the step value they name. This is the foundational skill -- every use-case skill from canvas_load_format_skill (e.g. diagramming) layers on top of it and assumes you already have it, so never load a format skill before this one. In a conversation, reuse each step's returned instructions instead of re-fetching them.",
    inputSchema: {
      type: "object",
      properties: {
        step: {
          default: null,
          description:
            "Workflow step selector. Omit on the first call; each response names the valid value for the next call.",
          type: "string",
          nullable: true,
        },
        miro_url: {
          default: null,
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      additionalProperties: false,
    },
    annotations: {
      title: "Canvas Composer",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "canvas_get_html_upload_url",
    description:
      "Reserve slots for uploading html_content documents out of band. Use this when a document is too large to send inline, or when an inline <html-source> is rejected in transit. PUT each document to its upload_url with Content-Type: text/html and no auth header, then author the widget with data-html-ref=\"<token>\" instead of an <html-source> child. Set count to the number of documents so the uploads can run in parallel. Max 1,048,576 bytes and UTF-8 per document; slots expire after the returned expires_in seconds. Uploaded HTML follows the inline rules: static HTML + CSS only, <script> and on* handlers are stripped; fonts and stylesheets via CDN links only (Google Fonts, cdnjs). IMPORTANT: local file references (e.g. './logo.png') anywhere in the HTML (src, srcset, url()) are not resolved; use https URLs. Avoid script.google.com URLs: the firewall rejects them with 403, and repeated 403s block the caller's IP, so remove the offending URL instead of retrying.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        count: {
          default: 1,
          description:
            "Number of upload slots to reserve, one per document. Max 30.",
          maximum: 30,
          minimum: 1,
          type: "integer",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Get HTML Upload URL",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "canvas_load_format_skill",
    description:
      "Load supplementary authoring guidance (a skill) for a specific composition format, layered ON TOP OF the general canvas format. PREREQUISITE: call canvas_get_canvas_composer_skill FIRST -- this tool assumes you already know the SVG board format and only adds format-specific styling and examples. Do not call it as your first step, and never in place of the composer skill. Content is still created with canvas_create_from_svg / canvas_update_from_svg. Available formats: 'diagramming' (styling and worked examples for diagrams: a Mermaid widget by default, or renderer='native' to draw the notation from editable shapes and connectors), 'presentation' (slide decks; available where the composer skill lists the SLIDE_CONTAINER widget), 'prototyping' (HTML authoring contract and design guidance for prototype screens; available where it lists the PROTOTYPING_CONTAINER widget) and 'tables' (columns, cells, layouts and editing rules for the TABLE widget). For 'diagramming', pass the notation you are drawing to get its color defaults and worked example. Call once per format/notation/renderer in a conversation and reuse the guidance.",
    inputSchema: {
      type: "object",
      properties: {
        format_name: {
          description:
            "The composition format to load guidance for. 'diagramming' for styling and worked examples of diagrams; 'presentation' for slide decks; 'prototyping' for HTML prototype screens; 'tables' for tables, kanbans and timelines.",
          enum: [
            "diagramming",
            "flows",
            "presentation",
            "prototyping",
            "tables",
          ],
          type: "string",
        },
        notation: {
          default: null,
          description:
            "For format_name='diagramming', the notation you are drawing: 'flowchart', 'entity_relationship', 'uml_class', 'uml_sequence', or 'free_form' for a hand-assembled diagram no notation fits (e.g. a mind map). Omit for the shared color contract and the list of notations.",
          enum: [
            "flowchart",
            "entity_relationship",
            "uml_class",
            "uml_sequence",
            "free_form",
            "bpmn",
          ],
          type: "string",
          nullable: true,
        },
        renderer: {
          default: null,
          description:
            "For format_name='diagramming': 'native' returns guidance to draw the notation from editable shapes and connectors; omit (mermaid) for the Mermaid diagram widget.",
          enum: ["mermaid", "native"],
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["format_name"],
      additionalProperties: false,
    },
    annotations: {
      title: "Load Format Skill",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "canvas_read_as_svg",
    description:
      "Read one board area or specific items as a canvas-composer SVG document. Provide widget_ids, all four scope fields, or both. Use canvas_search to find a scope. Use widget_ids only for specific items; selecting a container (a frame, slide deck, prototype, or diagram container) includes everything nested inside it. Has no target_id parameter (that belongs to canvas_search): name items with widget_ids and restrict to an area with the scope fields.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        widget_ids: {
          default: null,
          description:
            "Required unless all four scope fields are set. Return only the items with these data-miro-id values. Ancestor frames are added automatically so positions stay correct, and selecting a container (a frame, slide deck, prototype, or diagram container) pulls in everything nested inside it. Unknown ids are skipped.",
          items: {
            type: "string",
          },
          type: "array",
          nullable: true,
        },
        scope_x: {
          default: null,
          description: "Working area left edge.",
          type: "integer",
          nullable: true,
        },
        scope_y: {
          default: null,
          description: "Working area top edge.",
          type: "integer",
          nullable: true,
        },
        scope_width: {
          default: null,
          description: "Working area width.",
          type: "integer",
          nullable: true,
        },
        scope_height: {
          default: null,
          description: "Working area height.",
          type: "integer",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Read Board as SVG",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "canvas_search",
    description:
      "Search and navigate board content on top of the canvas-composer SVG representation to find relevant items and areas. Use canvas search beforehand to narrow down scope of a read.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        result_mode: {
          enum: ["overview", "areas", "matches"],
          type: "string",
          description:
            "Use overview without patterns for container titles and a small sample of board regions. Use areas to rank sections from related signals. Use matches for exact items, occurrences, IDs, attributes, or captures.",
        },
        patterns: {
          default: null,
          description:
            "Required for areas and matches. Omit for overview. Send related patterns together. Values are case-insensitive literal text by default. Prefix structural SVG regex with re:. Prefix literal text with text:. The prefix is consumed, so use text:text:budget to find the literal text:budget.",
          items: {
            maxLength: 500,
            minLength: 1,
            type: "string",
          },
          maxItems: 50,
          minItems: 1,
          type: "array",
          nullable: true,
        },
        target_id: {
          default: null,
          description:
            "Limit areas or matches to one result from an earlier search. Omit for overview.",
          maxLength: 256,
          pattern: "^m?\\d+$",
          type: "string",
          nullable: true,
        },
        cursor: {
          default: null,
          description:
            "Continue areas or matches from next_cursor only when the earlier result was truncated. Omit for overview.",
          minimum: 0,
          type: "integer",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "result_mode"],
      additionalProperties: false,
    },
    annotations: {
      title: "Search Canvas",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "canvas_update_from_svg",
    description:
      'Apply a canvas-composer SVG document to the board by diffing it against the live board (matched on data-miro-id) and applying only the deltas: it creates new elements, updates existing ones, and deletes elements explicitly marked with data-deleted="true" (which must carry the element\'s data-miro-id). Removing an element from the SVG does NOT delete it, deletion is explicit per element, so a partial document is always safe. Change part of a long body with <edit> children (see skill). Deletion is destructive and not undoable here: confirm the specific items with the user before sending any data-deleted="true". Feed the result_svg from a previous canvas_create_from_svg or canvas_update_from_svg call back in to iterate. Returns a result_svg with data-miro-id stamped on every element for the next iteration.',
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        svg: {
          description: "A board written in the Canvas Composer SVG format.",
          maxLength: 200000,
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "svg"],
      additionalProperties: false,
    },
    annotations: {
      title: "Update Board from SVG",
      readOnlyHint: false,
      destructiveHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "comment_create",
    description:
      "Create a new comment on the Miro board canvas. The comment appears at the specified canvas coordinates and is attributed to the current user. To attach the comment to an existing board item, pass a URL that targets that item. Use list_comments to read existing comments and their positions.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Miro board URL. Can be a plain board URL (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/') to get a board overview, or include moveToWidget/focusWidget parameter (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/?moveToWidget=3458764654510025479') to get detailed content for a specific item.",
          minLength: 1,
          type: "string",
        },
        content: {
          description: "Text content of the comment.",
          maxLength: 2000,
          minLength: 1,
          type: "string",
        },
        x: {
          description: "X-axis coordinate of the comment on the canvas.",
          type: "number",
        },
        y: {
          description: "Y-axis coordinate of the comment on the canvas.",
          type: "number",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "content", "x", "y"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Canvas Comment",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "comment_list_comments",
    description:
      "List comments from a Miro board or a specific item on the board. Comments include author information, messages (original comment and replies), reactions, resolved status, and position. Use limit and offset for pagination. Use from_date and to_date to filter by creation time. Use resolved to filter by resolved status.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Miro board URL. Can be a plain board URL (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/') to get a board overview, or include moveToWidget/focusWidget parameter (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/?moveToWidget=3458764654510025479') to get detailed content for a specific item.",
          minLength: 1,
          type: "string",
        },
        limit: {
          default: 20,
          description:
            "Maximum number of comments to return per page. Range: 1-50. Default: 20.",
          maximum: 50,
          minimum: 1,
          type: "integer",
        },
        offset: {
          default: 0,
          description: "Zero-based offset for pagination. Default: 0.",
          minimum: 0,
          type: "integer",
        },
        from_date: {
          default: null,
          description:
            "Return only comments created at or after this date. Format: ISO 8601 with optional time component, e.g. 2024-01-01T00:00:00Z or 2024-01-01.",
          type: "string",
          nullable: true,
        },
        to_date: {
          default: null,
          description:
            "Return only comments created at or before this date. Format: ISO 8601 with optional time component, e.g. 2024-12-31T23:59:59Z or 2024-12-31.",
          type: "string",
          nullable: true,
        },
        resolved: {
          default: null,
          description:
            "Filter by resolved status. true returns only resolved comments, false returns only unresolved. Omit to return all comments.",
          type: "boolean",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Read Comments",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "comment_reply",
    description:
      "Add a reply message to an existing comment thread on a Miro board. Use list_comments to find comment IDs. The reply appears as the last message in the thread and is attributed to the current user.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Miro board URL. Can be a plain board URL (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/') to get a board overview, or include moveToWidget/focusWidget parameter (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/?moveToWidget=3458764654510025479') to get detailed content for a specific item.",
          minLength: 1,
          type: "string",
        },
        comment_id: {
          description:
            "ID of the comment thread to reply to. Obtain from list_comments.",
          type: "string",
        },
        content: {
          description: "Text content of the reply message.",
          maxLength: 2000,
          minLength: 1,
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "comment_id", "content"],
      additionalProperties: false,
    },
    annotations: {
      title: "Reply to Comment",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "comment_resolve",
    description:
      "Resolve or unresolve a comment thread on a Miro board. Resolving marks the thread as addressed; unresolving reopens it. Use list_comments with resolved=false to find open threads.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Miro board URL. Can be a plain board URL (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/') to get a board overview, or include moveToWidget/focusWidget parameter (e.g., 'https://miro.com/app/board/uXjVGeTCXKY=/?moveToWidget=3458764654510025479') to get detailed content for a specific item.",
          minLength: 1,
          type: "string",
        },
        comment_id: {
          description:
            "ID of the comment thread to resolve or unresolve. Obtain from list_comments.",
          type: "string",
        },
        resolved: {
          description:
            "Set to true to resolve the thread, false to unresolve it.",
          type: "boolean",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "comment_id", "resolved"],
      additionalProperties: false,
    },
    annotations: {
      title: "Resolve Comment",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "content_item_list_roles",
    description:
      "List who can access a board or space and the role each of them holds. Use this to answer who a board or space is shared with, or what access somebody has. Each entry names the subject, its kind, the role it holds and, for users, their email address. User groups have no email, so identify them by their subject id. Entries whose kind is 'user' or 'user_group' and whose role is one of owner, coowner, editor, commenter or viewer can be fed straight into board_role_update or space_role_update. Access granted by other means is still listed, under its own kind or role name such as 'team' or 'private', and cannot be changed with those tools. Results are paginated: pass the returned cursor back to fetch the next page.",
    inputSchema: {
      type: "object",
      properties: {
        content_item_url: {
          description:
            "The board or space to inspect. Give a full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='), a full Miro space URL (e.g., 'https://miro.com/app/dashboard/space/0VM5RHCprOu2MnOHXLqp3f'), or the content item id of either on its own (e.g., '0VM5RHCprOu2MnOHXLqp3f') - a content item id identifies a board just as well as a space. A board key on its own is not accepted; use the board URL for that.",
          minLength: 1,
          type: "string",
        },
        limit: {
          default: null,
          description: "Maximum entries per page (1-100)",
          maximum: 100,
          minimum: 1,
          type: "integer",
          nullable: true,
        },
        cursor: {
          default: null,
          description: "Opaque cursor token for fetching the next page",
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["content_item_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "List Content Item Roles",
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "content_search",
    description:
      "Search Miro content the user can access: boards, spaces and video call recordings. Matches titles, descriptions and, for boards, the text inside them. Prefer this over search_boards whenever the target may not be a board. Returns each hit's Miro URL, type, name and plain-text snippets showing why it matched; recordings have no URL, so refer to them by name. Page with cursor and limit (max 50). An empty result means nothing matched: broaden the query rather than repeating it.",
    inputSchema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          minLength: 1,
          description:
            "Words to match against titles, descriptions and, for boards, the text written inside them. Start with the user's own phrasing.",
        },
        miro_type: {
          type: "array",
          items: {
            type: "string",
            enum: ["board", "space", "video_call_recording"],
          },
          description:
            "Restrict results to these types. Omit to search every type.",
        },
        space_url: {
          type: "string",
          minLength: 1,
          description:
            "Only return content inside this space, e.g. https://miro.com/app/dashboard/space/0VM5RHCprOu2MnOHXLqp3f.",
        },
        limit: {
          type: "integer",
          minimum: 1,
          maximum: 50,
          default: 20,
          description: "Maximum hits per page. Range: 1-50. Default: 20.",
        },
        cursor: {
          type: "string",
          description:
            "nextCursor from the previous response. Omit for the first page.",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["query"],
    },
    annotations: {
      title: "Search Miro Content",
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "create_boardcast",
    description:
      "Create a boardcast: a narrated, replayable walkthrough of a Miro board. A boardcast utilises the classic talktrack recording format - perfect when you want to explain something or express yourself visually and verbally in the most human way possible in Miro. Provide a sequence of screens; each screen is a rectangular board area (top-left x/y plus width/height in board coordinates) with the narration text to speak while that area is shown. Voice audio is generated automatically from the text. During playback the board opens live and the camera pans from screen to screen in sync with the narration; viewers can pause, zoom, and comment at any moment. Returns a playback link to share. Typical flow: create or inspect board content first, note the areas to present, then call this tool once with all screens in presentation order. Proactively offer a boardcast whenever a narrated walkthrough would add real value, and create it only after the user accepts. Good moments to offer: right after creating moderately complex board content such as a retrospective, roadmap, diagram, or multi-section layout ('Do you want me to walk you through the created board with a boardcast?'); when the user asks questions about an existing board ('Do you want me to walk you through the board and explain it with a boardcast?'); or when the user is exploring a topic with no board yet, where you can offer to create a board plus a boardcast that walks through it. Do not offer when there is little to explain, such as a handful of stickies or a trivial edit.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        screens: {
          description:
            "Screens in presentation order. Each screen: x, y (top-left corner of the board area to show), width, height (area size, controls zoom), text (narration spoken while the area is shown). Size the area with some padding around the content and keep a 16:9-ish aspect ratio for best framing.",
          items: {
            description:
              "A single screen of a talktrack: a board area to show plus its narration text.",
            properties: {
              x: {
                description:
                  "X coordinate of the top-left corner of the board area to show for this screen",
                maximum: 10000000,
                minimum: -10000000,
                type: "number",
              },
              y: {
                description:
                  "Y coordinate of the top-left corner of the board area to show for this screen",
                maximum: 10000000,
                minimum: -10000000,
                type: "number",
              },
              width: {
                default: 1920,
                description:
                  "Width of the board area to show (controls zoom level)",
                exclusiveMinimum: 0,
                maximum: 100000,
                type: "number",
              },
              height: {
                default: 1080,
                description:
                  "Height of the board area to show (controls zoom level)",
                exclusiveMinimum: 0,
                maximum: 100000,
                type: "number",
              },
              text: {
                description:
                  "Narration text spoken while this screen is shown. Write for speech: short sentences, no markdown, spell out abbreviations that should be read as words.",
                maxLength: 1500,
                minLength: 1,
                type: "string",
              },
            },
            required: ["x", "y", "text"],
            type: "object",
          },
          maxItems: 30,
          minItems: 1,
          type: "array",
        },
        name: {
          default: null,
          description:
            "Display name of the talktrack. Defaults to a timestamped name.",
          maxLength: 200,
          minLength: 1,
          type: "string",
          nullable: true,
        },
        voice: {
          default: null,
          description:
            "Narrator voice. Defaults to marin, a natural, warm US-English voice. Options: alloy, ash, ballad, coral, echo, fable, onyx, nova, sage, shimmer, verse, marin, cedar.",
          type: "string",
          nullable: true,
        },
        speed: {
          default: null,
          description:
            "Narration pace multiplier. 1.0 is the natural pace; below 1 slows the delivery down, above 1 speeds it up. Leave unset unless the pacing genuinely needs adjusting.",
          maximum: 4,
          minimum: 0.25,
          type: "number",
          nullable: true,
        },
        instructions: {
          default: null,
          description:
            "How the narration should be delivered: tone, energy, accent, emotional range, pacing style. Applies to the whole walkthrough, for example 'Warm, upbeat product demo narrator, confident and brisk' or 'Calm and measured, like a documentary voiceover'. Describe delivery only - the words spoken always come from each screen's text.",
          maxLength: 1000,
          minLength: 1,
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "screens"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Boardcast",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "get_brand_context",
    description:
      "Get the organization's brand identity for styling content you author: the brand theme (colors, typography), guidelines rendered for direct use, and a brand asset summary. Takes no arguments; scoped to the caller's organization and its default theme. Call at most once per session, when the request implies the organization's branding; styling the user specifies always wins. Empty sections mean default styling for that aspect; an empty result will not change within a session, so do not retry.",
    inputSchema: {
      type: "object",
      properties: {
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
    },
    annotations: {
      title: "Get Brand Context",
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "image_create",
    description:
      "Create an image item on a Miro board. Accepts either an upload token (from image_get_upload_url after the upload completes) or a publicly accessible image URL. Exactly one of image_token or image_url must be provided. When image_token is provided, title/x/y/width from the token (set at upload time) are used; any values supplied here for those fields are ignored. Coordinates: when the URL has no item target, x/y are board-absolute (board center is (0, 0)). When the URL targets a frame via moveToWidget, the image is created INSIDE that frame and x/y are relative to the frame's top-left corner; (0, 0) is the frame's top-left and the image must fit within the frame's width and height.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        image_token: {
          default: null,
          description:
            "Token returned by image_get_upload_url after the upload completes. Mutually exclusive with image_url.",
          type: "string",
          nullable: true,
        },
        image_url: {
          default: null,
          description:
            "Publicly accessible URL of the image to add to the board. Mutually exclusive with image_token.",
          type: "string",
          nullable: true,
        },
        title: {
          default: null,
          description: "Optional title label for the image widget.",
          type: "string",
          nullable: true,
        },
        x: {
          default: null,
          description:
            "X-axis coordinate. Without a frame target: board-absolute, board center is x=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is x=0).",
          type: "number",
          nullable: true,
        },
        y: {
          default: null,
          description:
            "Y-axis coordinate. Without a frame target: board-absolute, board center is y=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is y=0).",
          type: "number",
          nullable: true,
        },
        width: {
          default: null,
          description: "Image width in pixels. Height scales proportionally.",
          type: "number",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Image",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "image_get_data",
    description:
      "Get the pixels of an image item on a Miro board. Use this when a layout shows an image (by its properties and source URL) and you need to see what the image actually depicts. Returns the image content directly.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro URL to a specific item (e.g., 'https://miro.com/app/board/uXjVOakxTk0=/?moveToWidget=3458764516062720430'). Must include moveToWidget or focusWidget query parameter.",
          minLength: 1,
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Image Data",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "image_get_upload_url",
    description:
      "Get a single-use upload URL for a local image. Returns upload_url and a token. PUT the raw image bytes as the request body; set Content-Type to the image MIME type; no auth header. curl: curl -X PUT -H 'Content-Type: image/png' --data-binary @image.png '<upload_url>'. If the image only exists as in-memory bytes, write it to a local file first, then PUT that file. After upload, pass the returned token to any tool that accepts an image_token / image_tokens parameter — see each consumer tool's description for what it does with the upload. Max size: 6,000,000 bytes. Accepted types: image/bmp, image/gif, image/jpeg, image/png, image/svg+xml, image/vnd.adobe.photoshop.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        content_type: {
          default: null,
          description:
            "MIME type of the image to upload. Accepted: image/bmp, image/gif, image/jpeg, image/png, image/svg+xml, image/vnd.adobe.photoshop. When provided, the upload request Content-Type must match this value exactly. If omitted, content type is verified at image_create time.",
          type: "string",
          nullable: true,
        },
        title: {
          default: null,
          description: "Optional title label for the image widget.",
          type: "string",
          nullable: true,
        },
        x: {
          default: null,
          description:
            "X-axis coordinate. Without a frame target: board-absolute, board center is x=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is x=0).",
          type: "number",
          nullable: true,
        },
        y: {
          default: null,
          description:
            "Y-axis coordinate. Without a frame target: board-absolute, board center is y=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is y=0).",
          type: "number",
          nullable: true,
        },
        width: {
          default: null,
          description: "Image width in pixels. Height scales proportionally.",
          type: "number",
          nullable: true,
        },
        src: {
          default: null,
          description:
            "Only consumed by tools that rewrite HTML by src — currently prototype_create's and slides_create_html's image_tokens (i.e. a local file referenced from HTML). Set this to the EXACT string that appears as the src attribute in the HTML (e.g. './logo.png', 'assets/icon.svg') — matching is literal. The server rewrites every <img src=...> equal to this value with the uploaded board resource. Other consumers (image_create) ignore this field.",
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Get Upload URL",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "image_get_url",
    description: "Get image download URL for an image item from a Miro board.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro URL to a specific item (e.g., 'https://miro.com/app/board/uXjVOakxTk0=/?moveToWidget=3458764516062720430'). Must include moveToWidget or focusWidget query parameter.",
          minLength: 1,
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Download Image",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "prototype_create",
    description:
      "Create a Miro prototype from one or more HTML screens.\n\nImages: leave external http/https URLs in the HTML untouched — the server fetches and uploads them for you. ONLY local file references (e.g. './logo.png', 'assets/x.svg') need pre-upload: call image_get_upload_url with src set to the EXACT in-HTML reference, PUT the bytes, then pass the returned tokens via image_tokens below. The server rewrites matching <img src=...> attributes and attaches data-board-resource — do NOT rewrite the HTML yourself.\nIMPORTANT: ONLY local file references (e.g. './logo.png', 'assets/x.svg') in any part of the HTML like inline, url(), srcset, etc... REQUIRES pre-upload: call image_get_upload_url with src set to the EXACT in-HTML reference, PUT the bytes, then pass the returned tokens via image_tokens below.\nFonts: CDN URL only.\nAuthor static HTML + CSS only — <script> elements and inline event handlers (onclick etc.) are stripped, so any scripted behavior is silently lost; use CSS states (:hover, :focus) for visual interactivity.\nMax per screen: 1,048,576 bytes. Max screens per call: 20 — split larger prototypes across multiple prototype_create calls.\n\nProvide exactly one input — PREFER html_tokens (HTML stays out of context). html_contents is a last-resort fallback only when the runtime cannot issue an HTTP PUT at all; HTML size, image count, multi-screen, or not yet having the HTML saved to a file are NOT valid reasons — write it to a file yourself, then PUT it.\n\nPlacement: no item target → x/y are board-absolute (center is (0, 0)); moveToWidget on a frame → prototype is created inside, x/y are relative to frame top-left and must fit within frame width/height. All screens in one call share device_type, orientation, and placement; order is preserved.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        html_tokens: {
          default: null,
          description:
            "Preferred input. Ordered list of tokens from prototype_get_upload_url, one per screen. Use whenever the runtime can issue an HTTP PUT. Max 20 tokens per call.",
          items: {
            type: "string",
          },
          maxItems: 20,
          minItems: 1,
          type: "array",
          nullable: true,
        },
        html_contents: {
          default: null,
          description:
            "Last-resort fallback. Use ONLY when the runtime cannot issue an HTTP PUT at all (sandboxed agent with no network/curl/fetch). HTML size, image count, multi-screen, or not yet having the HTML saved to a file are NOT valid reasons — write the HTML to a file yourself and PUT it; html_tokens handles size/complexity by keeping the HTML out of context. Ordered list of raw HTML strings, one per screen; each at most 1,048,576 bytes. Max 20 screens per call.",
          items: {
            type: "string",
          },
          maxItems: 20,
          minItems: 1,
          type: "array",
          nullable: true,
        },
        image_tokens: {
          default: null,
          description:
            "REQUIRED whenever the HTML references images by a non-http(s) path (e.g. './logo.png', 'assets/x.svg', '/var/...'). Omitting tokens for such refs leaves broken images. External http/https URLs are fetched server-side and do NOT belong here. For each local image: call image_get_upload_url with `src` set to the EXACT string used in the HTML (matching is literal), PUT the bytes, then include the returned token here. The server rewrites every <img src=...> matching the stored src and attaches data-board-resource — do NOT rewrite the HTML yourself. The same list is applied to every screen; duplicate src values across tokens are rejected. May be omitted only when the HTML has zero local image refs.",
          items: {
            type: "string",
          },
          type: "array",
          nullable: true,
        },
        device_type: {
          default: "desktop",
          description:
            "Target device type for the prototype. Applies to every screen. Accepted values: 'desktop' (default), 'mobile', or 'tablet'.",
          enum: ["desktop", "mobile", "tablet"],
          type: "string",
        },
        orientation: {
          default: "landscape",
          description:
            "Screen orientation for the prototype. Applies to every screen. Accepted values: 'landscape' (default) or 'portrait'.",
          enum: ["landscape", "portrait"],
          type: "string",
        },
        x: {
          default: null,
          description:
            "X-axis coordinate. Without a frame target: board-absolute, board center is x=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is x=0).",
          type: "number",
          nullable: true,
        },
        y: {
          default: null,
          description:
            "Y-axis coordinate. Without a frame target: board-absolute, board center is y=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is y=0).",
          type: "number",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Prototype",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "prototype_get_upload_url",
    description:
      "Reserve one or more single-use upload slots for HTML screens. Set count to the number of screens in the prototype to reserve all slots in a single call instead of calling this once per screen. Returns one entry per slot, each with its own upload_url and token; uploads can run in parallel.\nPUT the raw HTML as the request body with Content-Type: text/html; no auth header.\ncurl: curl -X PUT -H 'Content-Type: text/html' --data-binary @page.html '<upload_url>'. If the HTML only exists as a string in your context, write it to a local file first (e.g. page.html), then PUT that file — not already having a file is not a reason to fall back to html_contents.\nMax size: 1,048,576 bytes per screen. External http/https image URLs are fine; the server fetches them. Only local file references need pre-upload (see prototype_create's image_tokens).\nAfter each upload, pass its token to prototype_create, in the same order.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        count: {
          default: 1,
          description:
            "Number of upload slots to reserve, one per screen. Request all slots needed for a prototype in a single call so uploads can run in parallel.",
          maximum: 20,
          minimum: 1,
          type: "integer",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Get HTML Upload URL",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "prototype_read",
    description:
      "Read prototype screens from a Miro board. Returns prototype screens with metadata (position, dimensions, device type) and HTML markup representing each screen's UI layout. Useful for AI tools to understand the design, structure, and navigation flow of interactive prototypes. Provide screen_id to read a specific screen, or omit to list all screens on the board. Recommended workflow: first list all screens with include_html=false (default) to get metadata, then read a specific screen with include_html=true to get its HTML markup.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
        },
        screen_id: {
          default: null,
          description:
            "Widget ID of a specific prototype screen to read. When omitted, all prototype screens on the board are returned.",
          type: "string",
          nullable: true,
        },
        include_html: {
          default: false,
          description:
            "Whether to include HTML markup for each screen. Defaults to false. Set to true only when HTML is needed for a specific screen to avoid large context payloads.",
          type: "boolean",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Read Prototype Screens",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "section_create",
    description:
      "Create a new section inside a space to group related boards and content. A section must live inside a space, so provide the space URL. If no title is given, a short one is generated from the user's goal. IMPORTANT: Always confirm with the user before creating a section.",
    inputSchema: {
      type: "object",
      properties: {
        space_url: {
          description:
            "A space, given either as a full Miro space URL (e.g., 'https://miro.com/app/dashboard/space/0VM5RHCprOu2MnOHXLqp3f') or as the space content item id on its own (e.g., '0VM5RHCprOu2MnOHXLqp3f'). Both the URL returned when a space is created and the content_item_id from a space listing are accepted.",
          minLength: 1,
          type: "string",
        },
        title: {
          default: null,
          description: "Title for the new section",
          maxLength: 60,
          type: "string",
          nullable: true,
        },
        order: {
          default: null,
          description:
            "Optional position among sibling sections; higher values are ordered first. When omitted, the section is placed on top.",
          type: "integer",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["space_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Section",
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "section_delete",
    description:
      "Delete a section. Boards inside the section are not deleted; they are moved up to the parent space. IMPORTANT: Always confirm with the user before deleting a section.",
    inputSchema: {
      type: "object",
      properties: {
        section_content_item_id: {
          description: "Identifier of the section to delete",
          minLength: 1,
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["section_content_item_id"],
      additionalProperties: false,
    },
    annotations: {
      title: "Delete Section",
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "section_update_metadata",
    description:
      "Rename a section or change its position among sibling sections. Provide at least one of a new title or a new order.",
    inputSchema: {
      type: "object",
      properties: {
        section_content_item_id: {
          description: "Identifier of the section to update",
          minLength: 1,
          type: "string",
        },
        title: {
          default: null,
          description: "New section title",
          maxLength: 60,
          type: "string",
          nullable: true,
        },
        order: {
          default: null,
          description:
            "New position among sibling sections; lower values are ordered first.",
          type: "integer",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["section_content_item_id"],
      additionalProperties: false,
    },
    annotations: {
      title: "Update Section Metadata",
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "table_create",
    description:
      "Create a table on a Miro board with specified columns. Supports text, select, multiselect, date, link, person, and number column types. This always creates a plain grid table. To produce a timeline, kanban, or tree, first create the table here, then call table_update_view to switch its layout. For a request like 'create a timeline/kanban/tree', do BOTH steps in sequence. Coordinates: when the URL has no item target, x/y are board-absolute (board center is (0, 0)). When the URL targets a frame via moveToWidget, the table is created INSIDE that frame and x/y are relative to the frame's top-left corner; (0, 0) is the frame's top-left and the table must fit within the frame's width and height. If no board URL is provided, a new board will be created. IMPORTANT: Always confirm with the user before creating a new board.",
    inputSchema: {
      type: "object",
      properties: {
        table_title: {
          description: "The title/name of the table",
          maxLength: 255,
          minLength: 1,
          type: "string",
        },
        columns: {
          description:
            'List of columns to create. At least one column must be provided.\n\n            Column types:\n            1. Text columns: Simple text fields\n\n            2. Select columns: Dropdown menus with predefined options, single value per row\n               - Each option needs a displayValue and color (hex code)\n               - column_type "select" is required whenever "options" is supplied\n\n            3. Multiselect columns: Like select, but a row can hold multiple options\n               - Each option needs a displayValue and color (hex code)\n               - column_type "multiselect" is required whenever "options" is supplied\n\n            4. Date columns: Date/time values in ISO 8601 format\n\n            5. Link columns: URLs with optional display text\n\n            6. Person columns: Assign Miro users by their user ID\n\n            7. Number columns: Numeric values with configurable decimal places\n               - Optional "decimals" (int, default 0) sets displayed decimal places\n\n            Examples:\n            [\n              {"column_type": "text", "column_title": "Name", "isTitle": true},\n              {"column_type": "text", "column_title": "Description"},\n              {"column_type": "date", "column_title": "Due Date"},\n              {"column_type": "link", "column_title": "Reference"},\n              {"column_type": "person", "column_title": "Assignee"},\n              {"column_type": "number", "column_title": "Estimate", "decimals": 1},\n              {\n                "column_type": "select",\n                "column_title": "Status",\n                "options": [\n                  {"displayValue": "To Do", "color": "#FF0000"},\n                  {"displayValue": "In Progress", "color": "#FFA500"},\n                  {"displayValue": "Done", "color": "#00FF00"}\n                ]\n              },\n              {\n                "column_type": "multiselect",\n                "column_title": "Tags",\n                "options": [\n                  {"displayValue": "Frontend", "color": "#FF0000"},\n                  {"displayValue": "Backend", "color": "#FFA500"}\n                ]\n              }\n            ]',
          items: {
            oneOf: [
              {
                description: "Input specification for a text column.",
                properties: {
                  column_type: {
                    const: "text",
                    default: "text",
                    type: "string",
                  },
                  column_title: {
                    description:
                      "Column name (e.g., 'Title', 'Description', 'Notes')",
                    maxLength: 255,
                    minLength: 1,
                    type: "string",
                  },
                  isTitle: {
                    default: false,
                    description:
                      "Set true to make this the table's primary title column. Every table has exactly one, and it cannot be deleted. Mark the most identifying text column (a name or short summary). If no text column is marked, the first text column is used; if there is no text column, a 'Title' column is added automatically.",
                    type: "boolean",
                  },
                },
                required: ["column_title", "column_type"],
                type: "object",
              },
              {
                description: "Input specification for a select column.",
                properties: {
                  column_type: {
                    const: "select",
                    default: "select",
                    type: "string",
                  },
                  column_title: {
                    description: "Column name (e.g., 'Status', 'Priority')",
                    maxLength: 255,
                    minLength: 1,
                    type: "string",
                  },
                  options: {
                    description: "List of options for the select dropdown",
                    items: {
                      description: "Input for select column option.",
                      properties: {
                        displayValue: {
                          description:
                            "Display text for this option (e.g., 'To Do', 'In Progress', 'Done')",
                          maxLength: 100,
                          minLength: 1,
                          type: "string",
                        },
                        color: {
                          description:
                            "Hex color code for this option (e.g., '#FF0000' for red, '#00FF00' for green)",
                          pattern: "^#[0-9A-Fa-f]{6}$",
                          type: "string",
                        },
                      },
                      required: ["displayValue", "color"],
                      type: "object",
                    },
                    maxItems: 50,
                    minItems: 1,
                    type: "array",
                  },
                },
                required: ["column_title", "options", "column_type"],
                type: "object",
              },
              {
                description: "Input specification for a multiselect column.",
                properties: {
                  column_type: {
                    const: "multiselect",
                    default: "multiselect",
                    type: "string",
                  },
                  column_title: {
                    description: "Column name (e.g., 'Tags', 'Labels')",
                    maxLength: 255,
                    minLength: 1,
                    type: "string",
                  },
                  options: {
                    description: "List of options for the multiselect dropdown",
                    items: {
                      description: "Input for select column option.",
                      properties: {
                        displayValue: {
                          description:
                            "Display text for this option (e.g., 'To Do', 'In Progress', 'Done')",
                          maxLength: 100,
                          minLength: 1,
                          type: "string",
                        },
                        color: {
                          description:
                            "Hex color code for this option (e.g., '#FF0000' for red, '#00FF00' for green)",
                          pattern: "^#[0-9A-Fa-f]{6}$",
                          type: "string",
                        },
                      },
                      required: ["displayValue", "color"],
                      type: "object",
                    },
                    maxItems: 50,
                    minItems: 1,
                    type: "array",
                  },
                },
                required: ["column_title", "options", "column_type"],
                type: "object",
              },
              {
                description: "Input specification for a date column.",
                properties: {
                  column_type: {
                    const: "date",
                    default: "date",
                    type: "string",
                  },
                  column_title: {
                    description: "Column name (e.g., 'Due Date', 'Start Date')",
                    maxLength: 255,
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["column_title", "column_type"],
                type: "object",
              },
              {
                description: "Input specification for a link column.",
                properties: {
                  column_type: {
                    const: "link",
                    default: "link",
                    type: "string",
                  },
                  column_title: {
                    description:
                      "Column name (e.g., 'Reference', 'Source URL')",
                    maxLength: 255,
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["column_title", "column_type"],
                type: "object",
              },
              {
                description: "Input specification for a person column.",
                properties: {
                  column_type: {
                    const: "person",
                    default: "person",
                    type: "string",
                  },
                  column_title: {
                    description: "Column name (e.g., 'Assignee', 'Owner')",
                    maxLength: 255,
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["column_title", "column_type"],
                type: "object",
              },
              {
                description: "Input specification for a number column.",
                properties: {
                  column_type: {
                    const: "number",
                    default: "number",
                    type: "string",
                  },
                  column_title: {
                    description:
                      "Column name (e.g., 'Estimate', 'Quantity', 'Price')",
                    maxLength: 255,
                    minLength: 1,
                    type: "string",
                  },
                  decimals: {
                    default: 0,
                    description: "Number of decimal places to display",
                    maximum: 10,
                    minimum: 0,
                    type: "integer",
                  },
                },
                required: ["column_title", "column_type"],
                type: "object",
              },
            ],
          },
          maxItems: 50,
          minItems: 1,
          type: "array",
        },
        x: {
          default: null,
          description:
            "X-axis coordinate for table placement. Without a frame target: board-absolute, board center is x=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is x=0). Use different x values to prevent overlap when creating multiple items.",
          type: "number",
          nullable: true,
        },
        y: {
          default: null,
          description:
            "Y-axis coordinate for table placement. Without a frame target: board-absolute, board center is y=0. With a frame target via moveToWidget: relative to the frame's top-left corner (frame top-left is y=0). Use different y values to prevent overlap when creating multiple items.",
          type: "number",
          nullable: true,
        },
        miro_url: {
          default: null,
          description:
            "Full Miro board URL (e.g., 'https://miro.com/app/board/uXjVOakxTk0='). If not provided, a new board will be created (requires user confirmation). To target a specific parent frame, include ?moveToWidget=<frame_id>.",
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["table_title", "columns"],
      additionalProperties: false,
    },
    annotations: {
      title: "Create Table",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "table_get_latest_update_history",
    description:
      "Get the history of a row's Latest Update field. The Latest Update field accumulates the text updates submitted for that row over time; this returns those entries ordered chronologically. Provide the table via its Miro URL and the target row via rowId (get rowIds from table_list_rows). \n\nResponse includes for each entry: text, author_id (Miro user ID of the author), created_at and modified_at (ISO 8601 timestamps), plus the total number of entries.",
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro URL to a specific item (e.g., 'https://miro.com/app/board/uXjVOakxTk0=/?moveToWidget=3458764516062720430'). Must include moveToWidget or focusWidget query parameter.",
          minLength: 1,
          type: "string",
        },
        row_id: {
          description:
            "Stable identifier of the row whose Latest Update field history to fetch. Get rowIds from table_list_rows.",
          pattern: "^[0-9a-zA-Z_-]{1,64}$",
          type: "string",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "row_id"],
      additionalProperties: false,
    },
    annotations: {
      title: "Read Latest Update History",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "table_list_rows",
    description:
      'Get rows from a Miro table with column metadata. Each row includes a stable rowId that uniquely identifies it within the table. rowIds persist across sorting, insertion, and deletion — use them to target specific rows in table_sync_rows. Supports filtering by column value. Returns text, select, multiselect, latest_update, and attachment columns (attachment columns are read-only and report filenames).\n\nBest practice: always use filter_by and limit when possible.Examples: next item that is not done: {"filter_by": {"Status":["To do", "In progress"]}, "limit": 1}top 5 high priority tasks: {"filter_by": {"Priority":["High"]}, "limit": 5}\n\nResponse includes for each row: rowId (stable row identifier), cells (array of columnTitle, valueType, content, options, latest_update_text, latest_update_author_id, attachments).\n\nPagination: Use \'limit\' to control page size (default 10) and \'next_cursor\' to fetch subsequent pages. The cursor is opaque and encodes pagination state.\n\nIMPORTANT: Do not change \'filter_by\' when using a cursor from a previous response, as this will result in an error. To apply a different filter, start a new pagination sequence (no cursor).',
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro URL to a specific item (e.g., 'https://miro.com/app/board/uXjVOakxTk0=/?moveToWidget=3458764516062720430'). Must include moveToWidget or focusWidget query parameter.",
          minLength: 1,
          type: "string",
        },
        filter_by: {
          default: null,
          description:
            'Filter rows by column value(s) in JSON format (case-insensitive). Only select and multiselect columns are supported. Format: JSON object mapping column names to arrays of display values. Example: {"Status": ["In Progress", "Done"]} matches rows where Status is either \'In Progress\' OR \'Done\'',
          type: "string",
          nullable: true,
        },
        next_cursor: {
          default: null,
          description: "Pagination cursor for the next set of results",
          type: "string",
          nullable: true,
        },
        limit: {
          default: null,
          description: "Maximum rows to return (minimum 1)",
          minimum: 1,
          type: "integer",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url"],
      additionalProperties: false,
    },
    annotations: {
      title: "Read Table",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "table_sync_rows",
    description:
      'Add or update rows in a Miro table.\n\nTo update existing rows, include rowId in the row object. rowId precisely targets a single row. Get rowIds from table_list_rows. Rows without rowId are inserted as new.\n\nExamples:\nUpdate a specific row by rowId: {"rows": [{"rowId": "3", "cells": [{"columnTitle": "Status", "value": "Complete"}]}]}\nInsert new rows: {"rows": [{"cells": [{"columnTitle": "Task", "value": "New task"}, {"columnTitle": "Status", "value": "Not Started"}]}]}',
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro URL to a specific item (e.g., 'https://miro.com/app/board/uXjVOakxTk0=/?moveToWidget=3458764516062720430'). Must include moveToWidget or focusWidget query parameter.",
          minLength: 1,
          type: "string",
        },
        rows: {
          description:
            'Array of rows to add or update. Include rowId to update an existing row.\n        Rows without rowId are inserted as new.\n\n        [{"rowId": "3", "cells": [{"columnTitle": "Status", "value": "Complete"}]}]\n        [{"cells": [{"columnTitle": "Task", "value": "Write tests"}, {"columnTitle": "Status", "value": "To Do"}]}]',
          items: {
            description: "Input for a single table row.",
            properties: {
              rowId: {
                default: null,
                description:
                  "Stable row identifier for targeting a specific row to update. Get rowIds from table_list_rows. Rows without rowId are inserted as new.",
                pattern: "^[0-9a-zA-Z_-]{1,64}$",
                type: "string",
                nullable: true,
              },
              cells: {
                description: "Array of cells in this row",
                items: {
                  description:
                    "Input for a single cell value.\n\nThe type is inferred from the column schema during processing.",
                  properties: {
                    columnTitle: {
                      description: "Title of the column this cell belongs to",
                      maxLength: 255,
                      minLength: 1,
                      type: "string",
                    },
                    value: {
                      anyOf: [
                        {
                          maxLength: 5000,
                          type: "string",
                        },
                        {
                          type: "number",
                        },
                        {
                          type: "integer",
                        },
                        {
                          items: {
                            type: "string",
                          },
                          maxItems: 5000,
                          type: "array",
                        },
                        {
                          items: {
                            additionalProperties: {
                              type: "string",
                            },
                            type: "object",
                          },
                          maxItems: 5000,
                          type: "array",
                        },
                      ],
                      description:
                        'Cell value. For text columns: string. For select columns: displayValue string. For multiselect columns: displayValue string or list of displayValue strings. For date columns: ISO 8601 date string (e.g., \'2025-07-30T22:00:00Z\'). For link columns: URL string or list of link objects [{"url": "...", "text": "..."}]For person columns: Miro user ID string or list of Miro user ID strings. For number columns: number or numeric string (e.g., 42 or \'3.14\'). Attachment columns are read-only and cannot be written.',
                    },
                  },
                  required: ["columnTitle", "value"],
                  type: "object",
                },
                type: "array",
              },
            },
            required: ["cells"],
            type: "object",
          },
          minItems: 1,
          type: "array",
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "rows"],
      additionalProperties: false,
    },
    annotations: {
      title: "Update Table",
      readOnlyHint: false,
      destructiveHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "table_update_view",
    description:
      'Update a Miro table widget\'s view: switch it to a grid table, timeline, or kanban board. The table keeps its data; only how it is displayed changes.\n\nChoose layout:\n- table: plain grid (use to revert from another layout)\n- timeline: lays records out on a time axis; optionally set the visible window with timeline_start_date and timeline_end_date\n- kanban: groups records into columns; set group_by_column to the name of a select column to group by (omit for an ungrouped board)\n\nOnly provide config that matches the chosen layout. Examples: switch to kanban grouped by Status: {"layout": "kanban", "group_by_column": "Status"}. Switch to a timeline for 2026: {"layout": "timeline", "timeline_start_date": "2026-01-01T00:00:00Z", "timeline_end_date": "2026-12-31T00:00:00Z"}.',
    inputSchema: {
      type: "object",
      properties: {
        miro_url: {
          description:
            "Full Miro URL to a specific item (e.g., 'https://miro.com/app/board/uXjVOakxTk0=/?moveToWidget=3458764516062720430'). Must include moveToWidget or focusWidget query parameter.",
          minLength: 1,
          type: "string",
        },
        layout: {
          description:
            "The layout to apply: 'table', 'timeline', 'kanban', or 'tree'.",
          enum: ["table", "timeline", "kanban", "tree", "journey"],
          type: "string",
        },
        group_by_column: {
          default: null,
          description:
            "KANBAN only: name of the select column to group cards into columns. Omit for an ungrouped board.",
          type: "string",
          nullable: true,
        },
        timeline_start_date: {
          default: null,
          description:
            "TIMELINE only: visible start of the timeline window in UTC format YYYY-MM-DDThh:mm:ssZ. This is the viewport, not a per-record date.",
          type: "string",
          nullable: true,
        },
        timeline_end_date: {
          default: null,
          description:
            "TIMELINE only: visible end of the timeline window in UTC format YYYY-MM-DDThh:mm:ssZ. This is the viewport, not a per-record date.",
          type: "string",
          nullable: true,
        },
        timeline_nesting_enabled: {
          default: null,
          description:
            "TIMELINE only: enable nested (hierarchical parent/child) rows.",
          type: "boolean",
          nullable: true,
        },
        timeline_dependencies_enabled: {
          default: null,
          description: "TIMELINE only: show dependency links between records.",
          type: "boolean",
          nullable: true,
        },
        timeline_date_unit: {
          default: null,
          description: "TIMELINE only: time-axis granularity.",
          enum: ["day", "week", "month", "quarter", "year"],
          type: "string",
          nullable: true,
        },
        table_nesting_enabled: {
          default: null,
          description:
            "TABLE only: enable nested (hierarchical parent/child) rows.",
          type: "boolean",
          nullable: true,
        },
        tree_direction: {
          default: null,
          description: "TREE only: direction the tree grows.",
          enum: ["top_to_bottom", "left_to_right"],
          type: "string",
          nullable: true,
        },
        view_name: {
          default: null,
          description:
            "Optional new display name for the view. Applies to any layout.",
          type: "string",
          nullable: true,
        },
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      required: ["miro_url", "layout"],
      additionalProperties: false,
    },
    annotations: {
      title: "Update Table View",
      readOnlyHint: false,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "user_who_am_i",
    description: "Returns the identity of the current authenticated user.",
    inputSchema: {
      type: "object",
      properties: {
        invocation_source: {
          type: "string",
          default: null,
          nullable: true,
          description:
            "Identifies what triggered this tool call. You must populate this field. Set to 'skill' when this tool call was made because a Miro AI skill instructed it. Set to 'ui' when invoked from the Miro MCP UI. Leave unset otherwise.",
        },
        is_repository: {
          type: "boolean",
          default: false,
          description:
            "Set to true when the folder you are operating in is a source-control repository (for example a Git working directory); set to false otherwise. You must populate this field. Defaults to false.",
        },
      },
      additionalProperties: false,
    },
    annotations: {
      title: "Who Am I",
      readOnlyHint: true,
      destructiveHint: false,
      openWorldHint: false,
    },
  },
];
